import {useEffect, useLayoutEffect, useRef} from 'react';

type ClickOutsideOptions = {
  shouldIgnore?: (
    event: MouseEvent | DocumentEventMap['touchstart']
  ) => boolean;
};

function useClickOutside(
  callback: (event?: MouseEvent | DocumentEventMap['touchstart']) => void,
  {shouldIgnore}: ClickOutsideOptions = {}
) {
  const ref = useRef<HTMLDivElement | null>(null);
  const refCallback = useRef(callback);
  const refShouldIgnore = useRef(shouldIgnore);

  useLayoutEffect(() => {
    refCallback.current = callback;
    refShouldIgnore.current = shouldIgnore;
  });

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent | DocumentEventMap['touchstart']
    ) => {
      const element = ref.current;
      if (element && !element.contains(event.target as Node)) {
        if (refShouldIgnore.current && refShouldIgnore.current?.(event)) {
          return;
        }
        refCallback.current(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return ref;
}

// Selectors for overlays that should keep a side panel open when clicked.
// MuiBackdrop-root covers modal backdrops; modal-content-root covers modal content.
// Both class names come from MUI/the design system — update here if they change.
const OVERLAY_SELECTORS = [
  '[class*="modal-content-root"]',
  '[class*="MuiBackdrop-root"]',
];

export function shouldIgnoreOverlayClick(
  event: MouseEvent | DocumentEventMap['touchstart']
): boolean {
  const target = event.target as Element;
  return OVERLAY_SELECTORS.some((selector) => !!target.closest?.(selector));
}

export default useClickOutside;
